import { useMemo, useState } from "react";
import { SearchIcon, Trash2 } from "lucide-react";

import { Button } from "@ui/shadCN/component/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/shadCN/component/select";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@ui/shadCN/component/input-group";

import { useObservationsCTX } from "pages/monitoring/hooks/useObservationsCTX";
import { ObservationSmallCard } from "pages/monitoring/outlets/initiatives/observations/search/ObservationSmallCard";
import { fuzzySearch } from "pages/monitoring/utils/search";
import { uiText } from "pages/monitoring/outlets/initiatives/observations/layout/uiText";

export function Search() {
  const { observations: allInitiativeObservations } = useObservationsCTX();
  const [lookFor, setLookFor] = useState("");
  const [biologicalGroup, setBiologicalGroup] = useState("");
  const [ecosystem, setEcosystem] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const {
    sortedObservations,
    presentBiologicalGroups,
    presentEcosystems,
    presentYears,
  } = useMemo(() => {
    const biologicalGroups: Record<number, string> = {};
    const ecosystems: Record<number, string> = {};
    const years = new Set<number>();

    const observationsWithMaxDate = allInitiativeObservations.map(
      (observation) => {
        let maxTimestamp = 0;

        for (const v of observation.versions) {
          const dateObj = new Date(v.creationDate);
          years.add(dateObj.getFullYear());

          const time = dateObj.getTime();
          if (time > maxTimestamp) {
            maxTimestamp = time;
          }
        }

        for (const { tag } of observation.tags) {
          if (tag.category.id === 3 && !biologicalGroups[tag.id]) {
            biologicalGroups[tag.id] = tag.name;
          } else if (tag.category.id === 4 && !ecosystems[tag.id]) {
            ecosystems[tag.id] = tag.name;
          }
        }

        return { observation, maxTimestamp };
      },
    );

    observationsWithMaxDate.sort((a, b) => b.maxTimestamp - a.maxTimestamp);

    return {
      sortedObservations: observationsWithMaxDate.map((i) => i.observation),
      presentBiologicalGroups: biologicalGroups,
      presentEcosystems: ecosystems,
      presentYears: years,
    };
  }, [allInitiativeObservations]);

  const sanitizedLookFor = useMemo(
    () =>
      lookFor
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase(),
    [lookFor],
  );

  const filterByBiologicalGroups =
    Object.keys(presentBiologicalGroups).length && biologicalGroup !== "";
  const filterByEcosystem =
    Object.keys(presentEcosystems).length && ecosystem !== "";
  const filterByYear = presentYears.size && selectedYear !== "";

  const filteredObservations = useMemo(() => {
    return sortedObservations.filter((observation) => {
      const sanitizedName = observation.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase();

      return (
        fuzzySearch(sanitizedLookFor, sanitizedName) &&
        (!filterByBiologicalGroups ||
          observation.tags.some(
            (tag) => tag.tag.id === Number(biologicalGroup),
          )) &&
        (!filterByEcosystem ||
          observation.tags.some((tag) => tag.tag.id === Number(ecosystem))) &&
        (!filterByYear ||
          observation.versions.some(
            (version) =>
              new Date(version.creationDate).getFullYear() ===
              Number(selectedYear),
          ))
      );
    });
  }, [
    sortedObservations,
    sanitizedLookFor,
    filterByBiologicalGroups,
    biologicalGroup,
    filterByEcosystem,
    ecosystem,
    filterByYear,
    selectedYear,
  ]);

  const clearFilters = () => {
    setLookFor("");
    setBiologicalGroup("");
    setEcosystem("");
    setSelectedYear("");
  };

  if (allInitiativeObservations.length === 0) {
    return null;
  }

  return (
    <div className="bg-grey-light border-b border-grey lg:border-b-0 flex-1 flex lg:min-w-76 lg:flex-col max-h-102 lg:max-h-screen">
      <div className="bg-primary/70 rounded-xl m-2 overflow-hidden [&>div]:px-4 space-y-2">
        <h3 className="bg-primary p-4 pb-2  m-0 font-normal text-primary-foreground">
          {uiText.search.module.title}
        </h3>

        <div className="pt-2">
          <label
            htmlFor="lookfor"
            className="text-primary-foreground font-normal"
          >
            {uiText.search.module.searchInputLabel}
          </label>
          <InputGroup>
            <InputGroupInput
              value={lookFor}
              onChange={(e) => setLookFor(e.target.value)}
              id="lookfor"
              autoComplete="off"
            />
            <InputGroupAddon align="inline-end">
              <SearchIcon className="text-accent" />
            </InputGroupAddon>
          </InputGroup>
        </div>

        <FilterSelect
          {...uiText.search.module.filterBiological}
          value={biologicalGroup}
          onChange={setBiologicalGroup}
          options={Object.entries(presentBiologicalGroups).map(
            ([key, value]) => ({ value: key, label: value }),
          )}
          shouldRender={Object.keys(presentBiologicalGroups).length > 1}
        />

        <FilterSelect
          {...uiText.search.module.filterEcosystem}
          value={ecosystem}
          onChange={setEcosystem}
          options={Object.entries(presentEcosystems).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          shouldRender={Object.keys(presentEcosystems).length > 1}
        />

        <FilterSelect
          {...uiText.search.module.filterYear}
          value={selectedYear}
          onChange={setSelectedYear}
          options={[...presentYears].map((y) => ({
            value: String(y),
            label: String(y),
          }))}
          shouldRender={presentYears.size > 1}
        />

        <div className="text-right pt-2 pb-4">
          <Button
            onClick={clearFilters}
            variant="outline_destructive"
            aria-label={uiText.search.module.resetSearchBtn.sr}
            title={uiText.search.module.resetSearchBtn.title}
          >
            <Trash2 />
            {uiText.search.module.resetSearchBtn.label}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-2 gap-4 overflow-auto scrollbar-custom">
        {filteredObservations.map((observation) => (
          <ObservationSmallCard
            key={`smallCartObservation_${observation.id}`}
            observation={observation}
          />
        ))}
      </div>
    </div>
  );
}

export function FilterSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
  shouldRender = true,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  shouldRender: boolean;
}) {
  if (!shouldRender) {
    return null;
  }

  const id = label.replaceAll(" ", "_");

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-primary-foreground font-normal">
        {label}
      </label>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="bg-background text-foreground">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent>
          {value !== "" && (
            <SelectItem value="" className="text-muted-foreground">
              Borrar seleccion
            </SelectItem>
          )}
          {options.map((opt) => (
            <SelectItem key={`${label}_opt_${opt.value}`} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
